import { useEffect, useRef, useState, Children } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchBookingDetail,
  fetchVerlauf,
  fetchAblage,
  postTransition,
  deleteBooking,
  postDocument,
  deleteAblageFile,
  downloadAblageFile,
  updateBooking,
  buildBookingInFromBooking,
  downloadEmailDraft,
  downloadKonusLinkEmailDraft,
  downloadKonusXml,
  postSendKonusLink,
  uploadMeldeschein,
  ALLOWED_TRANSITIONS,
  type VerlaufEntry,
  type AblageFile,
} from "@/api/bookingDetail";
import type { Booking, BookingPatch } from "@/api/bookings";
import { fetchUnits, type Unit } from "@/api/newBooking";
import { ANREDEN, ANREDEN_KONUS, GRUPPENARTEN, SAISONS, WORKFLOW_STATUS } from "@/api/constants";
import { syncDateRange } from "@/lib/dateRange";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { getSettingsConfig, getMailConfig } from "@/api/settings";
import { fetchCurrentUser } from "@/api/auth";
import { apiFetch, type ApiError } from "@/api/client";
import { Card } from "@/components/Card";
import { StatusPill } from "@/components/StatusPill";
import { Btn } from "@/components/Btn";
import { EField } from "@/components/EField";
import { RechnungDialog } from "@/components/RechnungDialog";
import { EurInput } from "@/components/EurInput";
import { MailPreviewModal } from "@/components/MailPreviewModal";
import { WorkflowProgress } from "@/components/WorkflowProgress";
import { ConflictModal, type ConflictEntry } from "@/components/dialogs/ConflictModal";
import { Modal } from "@/components/dialogs/Modal";
import { AdminEditDrawer } from "@/components/admin/AdminEditDrawer";
import { formatDatum } from "@/lib/format";
import { isValidElement } from "react";

function ConfirmDialog({
  icon,
  iconColor = "var(--status-cancel)",
  title,
  body,
  confirmLabel,
  cancelLabel = "Abbrechen",
  confirmVariant = "danger",
  onConfirm,
  onCancel,
}: {
  icon: string;
  iconColor?: string;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmVariant?: "danger" | "primary" | "secondary";
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span className="material-icons-outlined" style={{ color: iconColor, fontSize: 24 }}>{icon}</span>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--fg)" }}>{title}</h2>
      </div>
      <div style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 22, lineHeight: 1.5 }}>
        {body}
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onCancel}>{cancelLabel}</Btn>
        <Btn variant={confirmVariant} onClick={onConfirm}>{confirmLabel}</Btn>
      </div>
    </Modal>
  );
}

function PdfErsetzenDialog({
  art,
  onNeuErzeugen,
  onVorhandenesVersenden,
  onCancel,
}: {
  art: "bestaetigung" | "infos_gruppe";
  onNeuErzeugen: () => void;
  onVorhandenesVersenden: () => void;
  onCancel: () => void;
}) {
  const bezeichnung = art === "bestaetigung" ? "Buchungsbestätigung" : "Infos an Gruppe";
  return (
    <Modal maxWidth={480}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span className="material-icons-outlined" style={{ color: "var(--brand-700)", fontSize: 24 }}>picture_as_pdf</span>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--fg)" }}>Dokument bereits vorhanden</h2>
      </div>
      <div style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 22, lineHeight: 1.5 }}>
        Es liegt bereits eine <strong>{bezeichnung}</strong> in der Ablage vor.
        Soll ein neues Dokument erzeugt werden (ersetzt das bisherige) oder soll das vorhandene Dokument versandt werden?
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Btn variant="secondary" onClick={onCancel}>Abbrechen</Btn>
        <Btn variant="secondary" icon="send" onClick={onVorhandenesVersenden}>Vorhandenes versenden</Btn>
        <Btn variant="primary" icon="refresh" onClick={onNeuErzeugen}>Neu erzeugen & versenden</Btn>
      </div>
    </Modal>
  );
}

function StornoDialog({
  gruppenname,
  datum,
  onDatumChange,
  onConfirm,
  onCancel,
  pending,
}: {
  gruppenname: string;
  datum: string;
  onDatumChange: (d: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  pending: boolean;
}) {
  return (
    <Modal>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span className="material-icons-outlined" style={{ color: "var(--status-cancel)", fontSize: 24 }}>cancel</span>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--fg)" }}>Buchung stornieren</h2>
      </div>
      <div style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 18, lineHeight: 1.5 }}>
        Die Buchung <strong>{gruppenname}</strong> wird unwiderruflich auf{" "}
        <em>Storniert</em> gesetzt. Dieser Schritt kann nicht automatisch rückgängig gemacht werden.
      </div>
      <label style={{ display: "block", marginBottom: 18 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--fg-subtle)", fontWeight: 600, marginBottom: 6 }}>
          Stornierungsdatum
        </div>
        <input
          type="date"
          value={datum}
          onChange={(e) => onDatumChange(e.target.value)}
          style={{
            width: "100%",
            padding: "7px 10px",
            borderRadius: 6,
            border: "1.5px solid var(--border)",
            background: "var(--bg-input, var(--bg-subtle))",
            color: "var(--fg)",
            fontSize: 13,
            fontFamily: "var(--font-body)",
            boxSizing: "border-box",
          }}
        />
      </label>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onCancel} disabled={pending}>Abbrechen</Btn>
        <Btn variant="danger" onClick={onConfirm} disabled={pending || !datum}>Jetzt stornieren</Btn>
      </div>
    </Modal>
  );
}

type Tab = "overview" | "kontakt" | "buchung" | "konus" | "ablage" | "historie";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "overview", label: "Überblick",   icon: "dashboard" },
  { id: "kontakt",  label: "Kontakt",     icon: "person" },
  { id: "buchung",  label: "Buchung",     icon: "event" },
  { id: "konus",    label: "Konus-Daten", icon: "house" },
  { id: "ablage",   label: "Ablage",      icon: "folder" },
  { id: "historie", label: "Historie",    icon: "history" },
];

function naechte(checkIn: string, checkOut: string): number {
  return Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000);
}

// ---------------------------------------------------------------------------
// Haupt-Komponente
// ---------------------------------------------------------------------------

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const bookingId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  // Herkunft (z.B. /calendar oder /bookings?tab=storniert) für gezieltes Zurück;
  // Fallback auf die Standard-Buchungsübersicht bei Direkt-Aufruf ohne Historie.
  const backTo = (location.state as { from?: string } | null)?.from ?? "/bookings";
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>("overview");
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<BookingPatch>({});
  const [rechnungOpen, setRechnungOpen] = useState(false);
  const [conflictData, setConflictData] = useState<{ conflicts: ConflictEntry[]; body: Record<string, unknown> } | null>(null);
  const [stornoDialog, setStornoDialog] = useState<{ open: boolean; datum: string }>({
    open: false,
    datum: new Date().toISOString().slice(0, 10),
  });
  const [internConfirm, setInternConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ["me"], queryFn: fetchCurrentUser, retry: false });
  const isAdmin = (meQuery.data?.role || "").toLowerCase() === "admin";

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const { data: bk, isLoading, isError } = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => fetchBookingDetail(bookingId),
    retry: false,
    enabled: !isNaN(bookingId),
  });

  const { data: settingsConfig } = useQuery({
    queryKey: ["settings", "config"],
    queryFn: getSettingsConfig,
    staleTime: 5 * 60 * 1000,
  });
  const defaultAnzahlung = parseFloat(settingsConfig?.ANZAHLUNG_BETRAG ?? "100") || 100;

  const { data: verlauf = [] } = useQuery({
    queryKey: ["verlauf", bookingId],
    queryFn: () => fetchVerlauf(bookingId),
    enabled: !isNaN(bookingId),
    staleTime: 60 * 1000,
  });

  const { data: ablageFilesTop = [] } = useQuery({
    queryKey: ["ablage", bookingId],
    queryFn: () => fetchAblage(bookingId),
    enabled: !isNaN(bookingId),
    retry: false,
  });

  useDocumentTitle(
    bk
      ? `Buchung ${bk.buchungsreferenz}${bk.gastname ? " · " + bk.gastname : ""}`
      : "Buchung",
  );

  // Beim Wechsel der Buchung Draft zurücksetzen.
  useEffect(() => {
    if (!editing) setDraft({});
  }, [bookingId, editing]);

  const updateMutation = useMutation({
    mutationFn: ({ body, force }: { body: Record<string, unknown>; force: boolean }) =>
      updateBooking(bookingId, body, force),
    onSuccess: (updated) => {
      queryClient.setQueryData(["booking", bookingId], updated);
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      setEditing(false);
      setDraft({});
      setConflictData(null);
      setAdminError(null);
      setAdminOpen(false);
      showToast("Änderungen gespeichert");
    },
    onError: (err: unknown, variables) => {
      const apiErr = err as ApiError;
      if (apiErr?.status === 409) {
        const body = apiErr.body as { detail?: { conflicts?: ConflictEntry[] } };
        const cs = body?.detail?.conflicts;
        if (cs && cs.length > 0 && bk) {
          setConflictData({ conflicts: cs, body: variables.body });
          setAdminError(null);
          return;
        }
      }
      const message = (err as Error).message || "Fehler beim Speichern.";
      if (adminOpen) setAdminError(message);
      showToast(message, false);
    },
  });

  const startEdit = () => {
    setDraft({});
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft({});
    setEditing(false);
    setConflictData(null);
  };
  const saveEdit = () => {
    if (!bk) return;
    updateMutation.mutate({ body: buildBookingInFromBooking(bk, draft), force: false });
  };
  const forceSaveEdit = () => {
    if (!conflictData) return;
    updateMutation.mutate({ body: conflictData.body, force: true });
  };
  const handleAdminSave = (adminPatch: BookingPatch) => {
    if (!bk) return;
    setAdminError(null);
    updateMutation.mutate({ body: buildBookingInFromBooking(bk, adminPatch), force: false });
  };
  const patch = (p: BookingPatch) => setDraft((d: BookingPatch) => ({ ...d, ...p }));
  const dirty = Object.keys(draft).length > 0;

  // Der Anzeigewert nutzt im Edit-Mode den Draft, sonst die Buchung. Hilfreich,
  // damit im Header-Titel der gerade getippte Gruppenname sichtbar wird.
  const view: Booking | undefined = bk
    ? ({ ...bk, ...draft } as Booking)
    : undefined;

  const deleteMutation = useMutation({
    mutationFn: () => deleteBooking(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      navigate(backTo);
    },
    onError: (err: Error) => showToast(err.message || "Löschen fehlgeschlagen.", false),
  });

  const transitionMutation = useMutation({
    mutationFn: ({ target, storniert_am }: { target: string; storniert_am?: string }) =>
      postTransition(bookingId, target, storniert_am),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      //queryClient.setQueryData(["booking", bookingId], updated);
      showToast(`Status gesetzt: ${updated.status}`);
    },
    onError: (err: Error) => showToast(err.message, false),
  });

  const documentMutation = useMutation({
    mutationFn: (art: string) => postDocument(bookingId, art),
    onSuccess: (res) => {
      showToast(res.detail);
      queryClient.invalidateQueries({ queryKey: ["ablage", bookingId] });
      queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
    },
    onError: (err: Error) => showToast(err.message, false),
  });


  if (isLoading) {
    return <div style={{ padding: 40, color: "var(--fg-muted)", fontSize: 13 }}>Lade…</div>;
  }
  if (isError || !bk || !view) {
    return (
      <div style={{ padding: 40, color: "var(--status-cancel)", fontSize: 13 }}>
        Buchung konnte nicht geladen werden.
      </div>
    );
  }

  const anzeigeName = [view.vorname, view.nachname].filter(Boolean).join(" ") || view.gastname;
  const n = naechte(view.check_in, view.check_out);
  const personen = (view.personen_ab_16 ?? 0) + (view.kinder_6_bis_15 ?? 0) + (view.kinder_unter_6 ?? 0) || view.gaesteanzahl;
  const zeitraum = `${formatDatum(view.check_in)} – ${formatDatum(view.check_out)}`;

  const allowedTransitions = ALLOWED_TRANSITIONS[view.status] ?? [];

  return (
    <div>
      {adminOpen && bk && (
        <AdminEditDrawer
          bk={bk}
          onClose={() => { setAdminOpen(false); setAdminError(null); }}
          onSave={handleAdminSave}
          saving={updateMutation.isPending}
          errorMessage={adminError}
        />
      )}
      {conflictData && (
        <ConflictModal
          conflicts={conflictData.conflicts}
          headline={
            <>
              Die geänderten Daten kollidieren mit{" "}
              <strong>
                {conflictData.conflicts.length} bestehende
                {conflictData.conflicts.length !== 1 ? "n" : "r"} Buchung
                {conflictData.conflicts.length !== 1 ? "en" : ""}
              </strong>
              :
            </>
          }
          confirmLabel="Trotzdem speichern"
          onForce={forceSaveEdit}
          onCancel={() => setConflictData(null)}
        />
      )}
      {stornoDialog.open && (
        <StornoDialog
          gruppenname={view.gruppenname || view.gastname}
          datum={stornoDialog.datum}
          onDatumChange={(d) => setStornoDialog((s) => ({ ...s, datum: d }))}
          onConfirm={() => {
            const datum = stornoDialog.datum;
            setStornoDialog((s) => ({ ...s, open: false }));
            transitionMutation.mutate({ target: "Storniert", storniert_am: datum });
          }}
          onCancel={() => setStornoDialog((s) => ({ ...s, open: false }))}
          pending={transitionMutation.isPending}
        />
      )}
      {deleteConfirm && (
        <ConfirmDialog
          icon="delete_forever"
          title="Buchung endgültig löschen"
          body={
            <>
              Die Buchung <strong>{view.gruppenname || view.gastname}</strong>{" "}
              ({view.buchungsreferenz || `#${view.id}`}) wird <strong>unwiderruflich</strong>{" "}
              aus der Datenbank entfernt. Verlauf und zugehörige Datensätze gehen verloren.
              <br /><br />
              Möchtest du wirklich fortfahren?
            </>
          }
          confirmLabel="Endgültig löschen"
          confirmVariant="danger"
          onConfirm={() => {
            setDeleteConfirm(false);
            deleteMutation.mutate();
          }}
          onCancel={() => setDeleteConfirm(false)}
        />
      )}
      {internConfirm && (
        <ConfirmDialog
          icon="corporate_fare"
          iconColor="#607D8B"
          title="Als interne Buchung markieren"
          body={
            <>
              Die Buchung <strong>{view.gruppenname || view.gastname}</strong> wird als{" "}
              <em>Intern</em> markiert. Der Rechnungsbetrag wird auf <strong>0 €</strong> gesetzt.
              Interne Buchungen erscheinen im Kalender als Platzhalter und werden nicht abgerechnet.
            </>
          }
          confirmLabel="Als intern markieren"
          confirmVariant="primary"
          onConfirm={() => {
            setInternConfirm(false);
            transitionMutation.mutate({ target: "Intern" });
          }}
          onCancel={() => setInternConfirm(false)}
        />
      )}
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 100,
          padding: "12px 18px",
          background: toast.ok ? "var(--brand-700)" : "var(--status-cancel)",
          color: "#fff",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          boxShadow: "var(--sh-3)",
          maxWidth: 360,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Zurück-Link */}
      <button
        onClick={() => navigate(backTo)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--fg-muted)",
          fontSize: 12,
          fontFamily: "var(--font-body)",
          padding: 0,
          marginBottom: 16,
        }}
      >
        <span className="material-icons-outlined" style={{ fontSize: 16 }}>arrow_back</span>
        Zurück
      </button>

      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 16,
        marginBottom: 20,
        flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: "var(--fg-muted)",
                background: "var(--bg-subtle)",
                padding: "2px 8px",
                borderRadius: 4,
                border: "1px solid var(--border)",
              }}
            >
              {view.buchungsreferenz || `#${view.id}`}
            </span>
            <StatusPill status={view.status} />
          </div>
          <h1
            className="display"
            style={{
              fontSize: 30,
              margin: 0,
              color: "var(--brand-900)",
              fontWeight: 400,
              lineHeight: 1.1,
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {view.gruppenname || view.gastname}
          </h1>
          <div style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 6 }}>
            {anzeigeName} · {zeitraum} · {personen} Personen · {n} Nächte
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-end", minWidth: 700 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {!editing && allowedTransitions.includes("Intern") && (
              <Btn
                variant="secondary"
                icon="corporate_fare"
                onClick={() => setInternConfirm(true)}
                disabled={transitionMutation.isPending}
              >
                Intern
              </Btn>
            )}
            {!editing && isAdmin && (
              <Btn
                variant="danger"
                icon="delete_forever"
                onClick={() => setDeleteConfirm(true)}
                disabled={deleteMutation.isPending}
              >
                Löschen
              </Btn>
            )}
            {!editing && allowedTransitions.includes("Storniert") && (
              <Btn
                variant="danger"
                icon="cancel"
                onClick={() => setStornoDialog({ open: true, datum: new Date().toISOString().slice(0, 10) })}
                disabled={transitionMutation.isPending}
              >
                Stornieren
              </Btn>
            )}
            {!editing && isAdmin && (
              <button
                type="button"
                onClick={() => { setAdminError(null); setAdminOpen(true); }}
                title="Admin: alle Felder bearbeiten"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  height: 34,
                  background: "transparent",
                  border: "1px dashed color-mix(in oklab, var(--accent) 50%, var(--border-strong))",
                  color: "var(--accent)",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "var(--font-body)",
                }}
              >
                <span className="material-icons-outlined" style={{ fontSize: 16 }}>shield_person</span>
                Admin
              </button>
            )}
            {!editing ? (
              <Btn variant="secondary" icon="edit" onClick={startEdit}>
                Bearbeiten
              </Btn>
            ) : (
              <>
                <Btn variant="ghost" onClick={cancelEdit} disabled={updateMutation.isPending}>
                  Abbrechen
                </Btn>
                <Btn
                  variant="primary"
                  icon="save"
                  onClick={saveEdit}
                  disabled={!dirty || updateMutation.isPending}
                >
                  Änderungen speichern
                </Btn>
              </>
            )}
          </div>
          <div style={{ width: "100%" }}>
            <WorkflowProgress status={view.status} verlauf={verlauf} marginBottom={0} />
          </div>
        </div>
      </div>

      {editing && (
        <div style={{
          padding: "10px 14px",
          marginBottom: 16,
          background: "var(--brand-100)",
          border: "1px dashed var(--brand-700)",
          borderRadius: 8,
          fontSize: 13,
          color: "var(--brand-900)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <span className="material-icons-outlined" style={{ fontSize: 18 }}>edit</span>
          Bearbeitungsmodus aktiv — Änderungen werden erst beim Speichern wirksam.
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--border)", marginBottom: 20 }}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: "9px 14px",
                background: "none",
                border: "none",
                borderBottom: active ? "2px solid var(--brand-700)" : "2px solid transparent",
                marginBottom: -1,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                color: active ? "var(--brand-900)" : "var(--fg-muted)",
                fontFamily: "var(--font-body)",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "color .12s",
              }}
            >
              <span className="material-icons-outlined" style={{ fontSize: 16 }}>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <OverviewTab
          bk={view}
          bookingId={bookingId}
          n={n}
          personen={personen}
          editing={editing}
          patch={patch}
          onDocument={(art) => documentMutation.mutate(art)}
          documentPending={documentMutation.isPending}
          onOpenRechnung={() => setRechnungOpen(true)}
          onToast={showToast}
          onAnzahlungSave={(erhalten) => {
            if (!bk) return;
            updateMutation.mutate({
              body: buildBookingInFromBooking(bk, {
                anzahlung_erhalten: erhalten,
                anzahlung_erhalten_am: erhalten ? (bk.anzahlung_erhalten_am ?? new Date().toISOString().slice(0, 10)) : null,
              }),
              force: false,
            });
          }}
          anzahlungPending={updateMutation.isPending}
          defaultAnzahlung={defaultAnzahlung}
          onTransition={(target) => transitionMutation.mutate({ target })}
          transitionPending={transitionMutation.isPending}
          onResetVersandt={(felder) => {
            if (!bk) return;
            updateMutation.mutate({ body: buildBookingInFromBooking(bk, felder), force: false });
          }}
          resetVersandtPending={updateMutation.isPending}
          onStartEditing={startEdit}
        />
      )}
      {tab === "kontakt"  && <KontaktTab bk={view} editing={editing} patch={patch} />}
      {tab === "buchung"  && <BuchungTab bk={view} n={n} editing={editing} patch={patch} />}
      {tab === "konus"    && <KonusTab bk={view} bookingId={bookingId} editing={editing} patch={patch} onToast={showToast} />}
      {tab === "ablage"   && (
        <AblageTab
          bookingId={bookingId}
          onToast={showToast}
          onCreateRechnung={() => setRechnungOpen(true)}
        />
      )}
      {tab === "historie" && <HistorieTab bookingId={bookingId} />}

      {editing && (
        <div style={{
          position: "sticky",
          bottom: 0,
          marginTop: 24,
          padding: "12px 16px",
          background: "var(--bg-card)",
          borderTop: "1px solid var(--border)",
          boxShadow: "0 -4px 12px rgba(0,0,0,.05)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          zIndex: 10,
        }}>
          {dirty && (
            <span style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: "var(--brand-700)",
            }} />
          )}
          <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>
            {dirty ? "Ungespeicherte Änderungen" : "Keine Änderungen"}
          </span>
          <div style={{ flex: 1 }} />
          <Btn variant="ghost" onClick={cancelEdit} disabled={updateMutation.isPending}>
            Verwerfen
          </Btn>
          <Btn
            variant="primary"
            icon="save"
            onClick={saveEdit}
            disabled={!dirty || updateMutation.isPending}
          >
            Speichern
          </Btn>
        </div>
      )}

      <RechnungDialog
        bk={view}
        open={rechnungOpen}
        onClose={() => setRechnungOpen(false)}
        onToast={showToast}
        hasRechnung={ablageFilesTop.some((f: AblageFile) => f.dokument_art === "rechnung")}
      />

    </div>
  );
}

// ---------------------------------------------------------------------------
// Überblick-Tab
// ---------------------------------------------------------------------------

const EINHEIT_SCHLUESSEL: Array<{ name: string; field: keyof Booking; label: string }> = [
  { name: "EG", field: "schluesselcode_eg", label: "EG" },
  { name: "OG", field: "schluesselcode_og", label: "OG" },
  { name: "DG", field: "schluesselcode_dg", label: "DG" },
  { name: "LZ", field: "schluesselcode_lz", label: "LZ" },
];

// Group heading inside tab forms — small terracotta eyebrow + thin rule
function FieldGroup({ title, icon, hint, children, footer }: { title: string; icon?: string; hint?: string; children?: React.ReactNode; footer?: React.ReactNode }) {
return (
    <div>
      {/* Eyebrow-Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 0 8px",
          borderBottom: "1px solid var(--border)",
          marginBottom: 12,
        }}
      >
        {icon && (
          <span
            className="material-icons-outlined"
            style={{ fontSize: 16, color: "var(--brand-700)" }}
          >
            {icon}
          </span>
        )}
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".08em",
            color: "var(--brand-900)",
          }}
        >
          {title}
        </div>
        {hint && (
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginLeft: 4 }}>
            · {hint}
          </div>
        )}
      </div>

      {/* Grid mit Karten-Hülle pro Kind */}
      {children && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
          }}
        >
          {Children.map(children, (child) => {
            if (!isValidElement(child)) return child;
            // wide kommt vom EField-Prop — wir lesen es nur, ohne EField zu ändern
            const wide = (child.props as { wide?: boolean }).wide;
            return (
                <Card style={{ gridColumn: wide ? "1 / -1" : "auto" }}>
                  {child}
                </Card>
            );
          })}
        </div>
      )}
      {footer}
    </div>
  );
}

function OverviewTab({
  bk, bookingId, n, personen, patch, editing, documentPending, onOpenRechnung, onToast, onAnzahlungSave, anzahlungPending, defaultAnzahlung, onTransition, transitionPending, onResetVersandt, resetVersandtPending, onStartEditing,//onDocument
}: {
  bk: Booking;
  bookingId: number;
  n: number;
  personen: number;
  editing: boolean;
  patch: (p: import("@/api/bookings").BookingPatch) => void;
  onDocument: (art: string) => void;
  documentPending: boolean;
  onOpenRechnung: () => void;
  onToast: (msg: string, ok?: boolean) => void;
  onAnzahlungSave: (erhalten: boolean) => void;
  anzahlungPending: boolean;
  defaultAnzahlung: number;
  onTransition: (target: string) => void;
  transitionPending: boolean;
  onResetVersandt: (felder: import("@/api/bookings").BookingPatch) => void;
  resetVersandtPending: boolean;
  onStartEditing: () => void;
}) {
  const queryClient = useQueryClient();
  const einheiten = bk.einheiten_namen;

  const anzeigeName = [bk.vorname, bk.nachname].filter(Boolean).join(" ") || bk.gastname;

  const { data: ablageFiles = [] } = useQuery({
    queryKey: ["ablage", bookingId],
    queryFn: () => fetchAblage(bookingId),
    retry: false,
  });

  const [emailPending, setEmailPending] = useState(false);
  const [konusLinkPending, setKonusLinkPending] = useState(false);
  const [konusXmlPending, setKonusXmlPending] = useState(false);
  // pdfConfirm: kein Dokument vorhanden → Erzeugen-Dialog
  const [pdfConfirm, setPdfConfirm] = useState<{ art: "bestaetigung" | "infos_gruppe"; dokArt: string } | null>(null);
  // pdfErsetzenConfirm: Dokument bereits vorhanden → Ersetzen-oder-Vorhandenes-Dialog
  const [pdfErsetzenConfirm, setPdfErsetzenConfirm] = useState<{ art: "bestaetigung" | "infos_gruppe" } | null>(null);
  const [mailPreviewArt, setMailPreviewArt] = useState<string | null>(null);
  const [smtpWarnArt, setSmtpWarnArt] = useState<string | null>(null);
  const [konusMeldungDialogOpen, setKonusMeldungDialogOpen] = useState(false);
  const meldescheinInputRef = useRef<HTMLInputElement | null>(null);
  const [meldescheinPending, setMeldescheinPending] = useState(false);

  const meldescheinVorhanden = ablageFiles.some((f: AblageFile) => f.dokument_art === "meldeschein");

  const handleMeldescheinUpload = async (file: File) => {
    if (file.type && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      onToast("Nur PDF-Dateien sind erlaubt.", false);
      return;
    }
    setMeldescheinPending(true);
    try {
      await uploadMeldeschein(bookingId, file);
      await queryClient.invalidateQueries({ queryKey: ["ablage", bookingId] });
      onToast("Meldeschein hochgeladen.");
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : "Upload fehlgeschlagen.", false);
    } finally {
      setMeldescheinPending(false);
    }
  };
  // anzahlungWarn: Buchungsbestätigung ohne hinterlegte Anzahlung → Warnung zeigen
  const [anzahlungWarnNext, setAnzahlungWarnNext] = useState<{ art: "bestaetigung" | "infos_gruppe"; dokArt: string } | null>(null);

  const doEmailDraft = async (art: "bestaetigung" | "infos_gruppe") => {
    setEmailPending(true);
    try {
      const mailCfg = await getMailConfig();
      if (mailCfg.versand_modus === "smtp_preview") {
        if (!mailCfg.smtp_host || !(mailCfg.smtp_from || mailCfg.smtp_user)) {
          setSmtpWarnArt(art);
          return;
        }
        setMailPreviewArt(art);
        return;
      }
      await downloadEmailDraft(bookingId, art);
      onToast("E-Mail-Entwurf heruntergeladen — zum Öffnen doppelklicken.");
    } catch (e: unknown) {
      onToast((e instanceof Error ? e.message : "Fehler"), false);
    } finally {
      setEmailPending(false);
    }
  };

  const handleEmailDraft = async (art: "bestaetigung" | "infos_gruppe", dokArt: string) => {
    // Für Buchungsbestätigung: Warnung wenn keine Anzahlung hinterlegt
    if (art === "bestaetigung") {
      const az = bk.anzahlung_betrag;
      if (az === null || az === undefined || Number(az) === 0) {
        setAnzahlungWarnNext({ art, dokArt });
        return;
      }
    }
    _doHandleEmailDraft(art, dokArt);
  };

  const _doHandleEmailDraft = (art: "bestaetigung" | "infos_gruppe", dokArt: string) => {
    const hatPdf = ablageFiles.some((f: AblageFile) => f.dokument_art === dokArt);
    if (!hatPdf) {
      setPdfConfirm({ art, dokArt });
      return;
    }
    // Dokument existiert bereits → nachfragen
    setPdfErsetzenConfirm({ art });
  };

  const handlePdfConfirm = async () => {
    if (!pdfConfirm) return;
    const { art } = pdfConfirm;
    setPdfConfirm(null);
    setEmailPending(true);
    try {
      await postDocument(bookingId, art);
    } catch (e: unknown) {
      onToast((e instanceof Error ? e.message : "Fehler beim Erzeugen"), false);
      setEmailPending(false);
      return;
    }
    doEmailDraft(art);
  };

  // Nutzer wählt „Neu erzeugen": altes Dokument ersetzen
  const handlePdfErsetzen = async () => {
    if (!pdfErsetzenConfirm) return;
    const { art } = pdfErsetzenConfirm;
    setPdfErsetzenConfirm(null);
    setEmailPending(true);
    try {
      await postDocument(bookingId, art);
    } catch (e: unknown) {
      onToast((e instanceof Error ? e.message : "Fehler beim Erzeugen"), false);
      setEmailPending(false);
      return;
    }
    doEmailDraft(art);
  };

  // Nutzer wählt „Vorhandenes versenden": direkt Mail-Entwurf laden
  const handlePdfVorhandenes = () => {
    if (!pdfErsetzenConfirm) return;
    const { art } = pdfErsetzenConfirm;
    setPdfErsetzenConfirm(null);
    doEmailDraft(art);
  };

  const handleSendKonusLink = async () => {
    setKonusLinkPending(true);
    try {
      const mailCfg = await getMailConfig();
      if (mailCfg.versand_modus === "smtp_preview") {
        if (!mailCfg.smtp_host || !(mailCfg.smtp_from || mailCfg.smtp_user)) {
          setSmtpWarnArt("konus_link");
          return;
        }
        setMailPreviewArt("konus_link");
        return;
      }
      if (mailCfg.versand_modus === "eml_download") {
        await downloadKonusLinkEmailDraft(bookingId);
        onToast("E-Mail-Entwurf heruntergeladen — zum Öffnen doppelklicken.");
      } else {
        await postSendKonusLink(bookingId);
        onToast("Konus-Link erfolgreich versandt.");
      }
    } catch (e: unknown) {
      onToast((e instanceof Error ? e.message : "Fehler beim Versand"), false);
    } finally {
      setKonusLinkPending(false);
    }
  };

  const handleKonusXmlErzeugen = async () => {
    const fehlend = pruefePflichtfelder(bk);
    if (fehlend.length > 0) {
      onToast(`Pflichtfelder fehlen: ${fehlend.join(", ")}`, false);
      return;
    }
    setKonusXmlPending(true);
    try {
      const fileName = await downloadKonusXml(bookingId);
      onToast(`Konus-XML erzeugt: ${fileName}`);
      queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      queryClient.invalidateQueries({ queryKey: ["ablage", bookingId] });
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : "Fehler beim Erzeugen der XML.", false);
    } finally {
      setKonusXmlPending(false);
    }
  };

  const isPending = documentPending || emailPending || konusLinkPending || konusXmlPending;

  const AKTIONEN: {
    icon: string;
    label: string;
    meta: string;
    action: () => void;
    transitionTarget?: string;
    aktivBeiStatus?: string;
    versandtAm?: string | null;
    resetFelder?: import("@/api/bookings").BookingPatch;
  }[] = [
    {
      icon: "mark_email_unread",
      label: "Buchungsbestätigung versenden",
      meta: "E-Mail-Entwurf mit PDF-Anhang",
      action: () => handleEmailDraft("bestaetigung", "buchungsbestaetigung"),
      transitionTarget: "Bestätigt",
      aktivBeiStatus: "Angelegt",
      versandtAm: bk.bestaetigung_extern_versandt ? bk.bestaetigung_extern_versandt_am : null,
      resetFelder: { bestaetigung_extern_versandt: false, bestaetigung_extern_versandt_am: null },
    },
    {
      icon: "link",
      label: "Konus-Link versenden",
      meta: "Gäste-Formular-Link per E-Mail",
      action: handleSendKonusLink,
      transitionTarget: "Konus Link versandt",
      aktivBeiStatus: "Bestätigt",
      versandtAm: bk.konus_link_versandt ? bk.konus_link_versandt_am : null,
      resetFelder: { konus_link_versandt: false, konus_link_versandt_am: null },
    },
    {
      icon: "assignment_turned_in",
      label: "Konus-Daten bei der Behörde eintragen",
      meta: "XML erzeugen und herunterladen",
      action: handleKonusXmlErzeugen,
      transitionTarget: "Konus-Daten eingegeben",
      aktivBeiStatus: "Konus Link versandt",
      versandtAm: bk.konus_daten_eingetragen ? bk.konus_daten_eingetragen_am : null,
      resetFelder: { konus_daten_eingetragen: false, konus_daten_eingetragen_am: null },
    },
    {
      icon: "info",
      label: "Infos an Gruppe versenden",
      meta: "E-Mail-Entwurf mit PDF-Anhang",
      action: () => handleEmailDraft("infos_gruppe", "infos_gruppe"),
      transitionTarget: "Infos an Gruppe versandt",
      aktivBeiStatus: "Konus-Daten eingegeben",
      versandtAm: bk.infos_gruppe_extern_versandt ? bk.infos_gruppe_extern_versandt_am : null,
      resetFelder: { infos_gruppe_extern_versandt: false, infos_gruppe_extern_versandt_am: null },
    },
    {
      icon: "receipt_long",
      label: "Rechnung versenden",
      meta: "Rechnungsdialog öffnen",
      action: onOpenRechnung,
      transitionTarget: "Rechnung erstellt",
      aktivBeiStatus: "Abgereist",
      versandtAm: bk.rechnung_versandt_am ?? null,
      resetFelder: { rechnung_versandt_am: null },
    },
    {
      icon: "forward_to_inbox",
      label: "Freie E-Mail",
      meta: "an Kontaktadresse senden",
      action: () => { window.location.href = `mailto:${bk.email}`; },
    },
  ];

  const [resetConfirm, setResetConfirm] = useState<{ label: string; felder: import("@/api/bookings").BookingPatch } | null>(null);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16 }}>
      {resetConfirm && (
        <ConfirmDialog
          icon="undo"
          iconColor="var(--fg-muted)"
          title="Versand zurücksetzen"
          body={<>Das Datum für <strong>{resetConfirm.label}</strong> wird gelöscht. Der Status bleibt unverändert.</>}
          confirmLabel="Zurücksetzen"
          confirmVariant="secondary"
          onConfirm={() => { onResetVersandt(resetConfirm.felder); setResetConfirm(null); }}
          onCancel={() => setResetConfirm(null)}
        />
      )}
      {anzahlungWarnNext && (
        <ConfirmDialog
          icon="payments"
          iconColor="var(--status-warn, #d97706)"
          title="Keine Anzahlung hinterlegt"
          body={
            <>
              Für diese Buchung ist <strong>keine Anzahlung</strong> eingetragen (oder 0 €).
              Die Buchungsbestätigung wird dann <em>ohne</em> Anzahlungsaufforderung erzeugt.
              <br /><br />
              Möchtest du die Anzahlung zuerst nachtragen oder trotzdem fortfahren?
            </>
          }
          confirmLabel="Trotzdem fortfahren"
          cancelLabel="Anzahlung nachtragen"
          confirmVariant="secondary"
          onConfirm={() => {
            const next = anzahlungWarnNext;
            setAnzahlungWarnNext(null);
            _doHandleEmailDraft(next.art, next.dokArt);
          }}
          onCancel={() => {
            setAnzahlungWarnNext(null);
            onStartEditing();
          }}
        />
      )}
      {pdfConfirm && (
        <ConfirmDialog
          icon="picture_as_pdf"
          iconColor="var(--brand-700)"
          title="PDF noch nicht vorhanden"
          body={
            <>
              Es liegt noch keine{" "}
              <strong>{pdfConfirm.art === "bestaetigung" ? "Buchungsbestätigung" : "Infos-an-Gruppe"}</strong>
              {" "}als PDF vor. Soll das Dokument jetzt erzeugt und der E-Mail-Entwurf geladen werden?
            </>
          }
          confirmLabel="PDF erzeugen & E-Mail laden"
          confirmVariant="primary"
          onConfirm={handlePdfConfirm}
          onCancel={() => setPdfConfirm(null)}
        />
      )}
      {pdfErsetzenConfirm && (
        <PdfErsetzenDialog
          art={pdfErsetzenConfirm.art}
          onNeuErzeugen={handlePdfErsetzen}
          onVorhandenesVersenden={handlePdfVorhandenes}
          onCancel={() => setPdfErsetzenConfirm(null)}
        />
      )}
      {konusMeldungDialogOpen && (
        <KonusMeldungDialog
          bookingId={bookingId}
          onClose={() => setKonusMeldungDialogOpen(false)}
          onSuccess={() => {
            setKonusMeldungDialogOpen(false);
            queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
            queryClient.invalidateQueries({ queryKey: ["verlauf", bookingId] });
            onToast("Meldescheinnummer eingetragen, Status gesetzt.");
          }}
          onToast={onToast}
        />
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Kollisions-Banner */}
        {bk.hat_konflikt && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            background: "var(--warn-bg)",
            border: "1px solid var(--warn-border)",
            borderRadius: 8,
            fontSize: 13,
            color: "var(--warn-fg)",
          }}>
            <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--warn-icon)", flexShrink: 0 }}>warning</span>
            <span><strong>Belegungskonflikt:</strong> Diese Buchung überschneidet sich mit einer anderen Buchung im selben Zeitraum.</span>
          </div>
        )}

        {/* Abgereist-Banner */}
        {bk.status === "Infos an Gruppe versandt" && new Date(bk.check_out) < new Date(new Date().toDateString()) && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "12px 16px",
            background: "var(--note-bg)",
            border: "1px solid var(--note-border)",
            borderRadius: 8,
            fontSize: 13,
            color: "var(--note-fg)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--note-icon)" }}>schedule</span>
              <span>Abreisedatum liegt in der Vergangenheit — Gruppe abgereist?</span>
            </div>
            <button
              onClick={() => onTransition("Abgereist")}
              disabled={transitionPending}
              style={{
                background: "var(--brand-700)",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: transitionPending ? "default" : "pointer",
                whiteSpace: "nowrap",
                opacity: transitionPending ? 0.6 : 1,
              }}
            >
              Als Abgereist markieren
            </button>
          </div>
        )}

        {/* Meldeschein-Hinweis */}
        {bk.status === "Konus-Daten eingegeben" && !meldescheinVorhanden && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "12px 16px",
            background: "var(--note-bg)",
            border: "1px solid var(--note-border)",
            borderRadius: 8,
            fontSize: 13,
            color: "var(--note-fg)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--note-icon)" }}>fact_check</span>
              <span>Meldeschein der Gemeinde fehlt noch — bitte hochladen.</span>
            </div>
            <input
              ref={meldescheinInputRef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: "none" }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) void handleMeldescheinUpload(f);
                e.target.value = "";
              }}
            />
            <button
              onClick={() => meldescheinInputRef.current?.click()}
              disabled={meldescheinPending}
              style={{
                background: "var(--brand-700)",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: meldescheinPending ? "default" : "pointer",
                whiteSpace: "nowrap",
                opacity: meldescheinPending ? 0.6 : 1,
              }}
            >
              {meldescheinPending ? "Lädt hoch…" : "Meldeschein hochladen"}
            </button>
          </div>
        )}

        {/* Summary Tiles */}
        <Card>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <Tile label="Anreise" value={formatDatum(bk.check_in)} meta={(bk.check_in_time || "15:00") + " Uhr"} />
            <Tile label="Abreise" value={formatDatum(bk.check_out)} meta={(bk.check_out_time || "10:00") + " Uhr"} />
            <Tile
              label="Personen"
              value={String(personen)}
              meta={`${bk.personen_ab_16 ?? 0} · ${bk.kinder_6_bis_15 ?? 0} · ${bk.kinder_unter_6 ?? 0}`}
            />
            <Tile label="Nächte" value={String(n)} meta={bk.saison || ""} />
          </div>
        </Card>

        {/* Schlüsselcodes */}
        <Card padded={false}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", fontSize: 14, fontWeight: 600 }}>
            Schlüsselcodes
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0 }}>
            {EINHEIT_SCHLUESSEL.map(({ name, field, label }) => {
              const gebucht = einheiten.includes(name);
              const rawVal = bk[field] as string | null;
              if (editing && gebucht) {
                return (
                  <SchluesselCell
                    key={label}
                    label={label}
                    value={rawVal}
                    onChange={(v) => patch({ [field]: v || null } as import("@/api/bookings").BookingPatch)}
                  />
                );
              }
              return (
                <InfoCell key={label} label={label} value={gebucht ? (rawVal || "—") : "—"} />
              );
            })}
          </div>
        </Card>

        {/* Kontakt-Summary */}
        <Card padded={false}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", fontSize: 14, fontWeight: 600 }}>
            Kontakt
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0 }}>
            <InfoCell label="Ansprechpartner" value={`${bk.anrede} ${anzeigeName}`.trim()} />
            <InfoCell label="E-Mail" value={bk.email} />
            <InfoCell label="Telefon" value={bk.telefon || "—"} />
            <InfoCell label="Gruppenart" value={bk.gruppenart || "—"} />
            <InfoCell
              label="Adresse"
              value={[bk.adresse_strasse, [bk.adresse_plz, bk.adresse_ort].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—"}
            />
          </div>
        </Card>

        {/* Bemerkung */}
        {bk.bemerkung && (
          <Card>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--fg-subtle)", fontWeight: 600, marginBottom: 6 }}>
              Bemerkung
            </div>
            <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{bk.bemerkung}</div>
          </Card>
        )}

        {/* Einheiten */}
        {einheiten.length > 0 && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Einheiten</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {einheiten.map(e => {
                const colorMap: Record<string, string> = {
                  "EG":  "var(--unit-eg)",
                  "OG": "var(--unit-og)",
                  "Zeltplatz":    "var(--unit-zp)",
                  "DG": "var(--unit-dg)",
                  "LZ": "var(--unit-lz)",
                };
                const color = colorMap[e] ?? "var(--brand-700)";
                return (
                  <div key={e} style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "var(--bg-subtle)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                  }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{e}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Rechte Spalte */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Aktionen */}
        <Card padded={false}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", fontSize: 14, fontWeight: 600 }}>
            Aktionen
          </div>
          {AKTIONEN.map((a) => {
            const versandtDatum = a.versandtAm ? formatDatum(a.versandtAm) : null;
            const istVersandt = a.versandtAm != null;
            const kannBestaetigen = a.transitionTarget != null && a.aktivBeiStatus === bk.status && !istVersandt;
            return (
              <div key={a.label} style={{ borderBottom: "1px solid var(--border)" }}>
                <button
                  onClick={a.action}
                  disabled={isPending}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 12,
                    width: "100%",
                    padding: "8px 16px",
                    background: "none",
                    border: "none",
                    borderBottom: a.transitionTarget ? "1px solid var(--border-subtle, var(--border))" : "none",
                    cursor: isPending ? "default" : "pointer",
                    textAlign: "left",
                    color: "var(--fg)",
                    fontFamily: "var(--font-body)",
                    transition: "background .12s",
                    opacity: isPending ? 0.6 : 1,
                    boxSizing: "border-box",
                  }}
                  onMouseEnter={e => !isPending && (e.currentTarget.style.background = "var(--bg-hover)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--brand-700)", flexShrink: 0 }}>{a.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.label}</div>
                    <div style={{ fontSize: 11, color: "var(--fg-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.meta}</div>
                  </div>
                  <span className="material-icons-outlined" style={{ fontSize: 16, color: "var(--fg-subtle)", flexShrink: 0 }}>chevron_right</span>
                </button>
                {a.transitionTarget && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 16px", background: "var(--bg-subtle)" }}>
                    <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>Versand bestätigen</span>
                    {istVersandt ? (
                      <button
                        onClick={() => a.resetFelder && setResetConfirm({ label: a.label, felder: a.resetFelder })}
                        disabled={resetVersandtPending || !a.resetFelder}
                        title={a.resetFelder ? "Klicken zum Zurücksetzen" : undefined}
                        style={{
                          display: "flex", alignItems: "center", gap: 4,
                          background: "var(--status-ok, #22c55e)", color: "#fff",
                          borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600,
                          border: "none", cursor: a.resetFelder && !resetVersandtPending ? "pointer" : "default",
                          opacity: resetVersandtPending ? 0.6 : 1,
                          fontFamily: "var(--font-body)",
                        }}
                      >
                        <span className="material-icons-outlined" style={{ fontSize: 14 }}>check_circle</span>
                        Versandt{versandtDatum ? `: ${versandtDatum}` : ""}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!kannBestaetigen) return;
                          if (a.transitionTarget === "Konus-Daten eingegeben") {
                            if (!bk.meldescheinnummer || !bk.meldescheinnummer.trim()) {
                              setKonusMeldungDialogOpen(true);
                            } else {
                              onTransition(a.transitionTarget);
                            }
                          } else {
                            onTransition(a.transitionTarget!);
                          }
                        }}
                        disabled={!kannBestaetigen || transitionPending}
                        title={kannBestaetigen ? "Versand bestätigen und Status wechseln" : `Nur möglich bei Status "${a.aktivBeiStatus}"`}
                        style={{
                          display: "flex", alignItems: "center", gap: 4,
                          background: kannBestaetigen ? "var(--bg-card)" : "var(--bg-subtle)",
                          border: `1.5px solid ${kannBestaetigen ? "var(--border)" : "var(--border)"}`,
                          borderRadius: 6, padding: "4px 10px",
                          cursor: kannBestaetigen && !transitionPending ? "pointer" : "default",
                          fontSize: 12, fontWeight: 600,
                          color: kannBestaetigen ? "var(--fg)" : "var(--fg-subtle)",
                          opacity: kannBestaetigen ? 1 : 0.5,
                        }}
                      >
                        <span className="material-icons-outlined" style={{ fontSize: 14 }}>radio_button_unchecked</span>
                        Ausstehend
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </Card>

        {/* Rechnung (Anzahlung + Gesamtpreis) */}
        <RechnungCard
          bk={bk}
          editing={editing}
          patch={patch}
          onAnzahlungConfirm={onAnzahlungSave}
          anzahlungPending={anzahlungPending}
          defaultAnzahlung={defaultAnzahlung}
          onTransition={onTransition}
          transitionPending={transitionPending}
          setResetConfirm={setResetConfirm}
          resetVersandtPending={resetVersandtPending}
        />
      </div>

      {smtpWarnArt && (
        <ConfirmDialog
          icon="warning"
          iconColor="var(--status-warn, #d97706)"
          title="SMTP nicht vollständig konfiguriert"
          body={
            <>
              Der SMTP-Versand ist aktiviert, aber <strong>Server oder Absender</strong> fehlen noch.
              Bitte zuerst die{" "}
              <a href="/app/settings" style={{ color: "var(--brand-700)" }}>Mail-Einstellungen</a>
              {" "}vervollständigen.
            </>
          }
          confirmLabel="Trotzdem öffnen"
          confirmVariant="secondary"
          onConfirm={() => { setMailPreviewArt(smtpWarnArt); setSmtpWarnArt(null); }}
          onCancel={() => { setSmtpWarnArt(null); setEmailPending(false); setKonusLinkPending(false); }}
        />
      )}
      {mailPreviewArt && (
        <MailPreviewModal
          bookingId={bookingId}
          art={mailPreviewArt}
          onClose={() => { setMailPreviewArt(null); setEmailPending(false); setKonusLinkPending(false); }}
          onToast={onToast}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
            queryClient.invalidateQueries({ queryKey: ["verlauf", bookingId] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kontakt-Tab
// ---------------------------------------------------------------------------

function KontaktTab({
  bk,
  editing,
  patch,
}: {
  bk: Booking;
  editing: boolean;
  patch: (p: BookingPatch) => void;
}) {
  return (
      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        <FieldGroup title="Gruppe" icon="groups" hint={undefined}>
          <EField label="Gruppenname" value={bk.gruppenname} editing={editing} onChange={(v) => patch({ gruppenname: v })} />
        </FieldGroup>
        <FieldGroup title="Ansprechpartner" icon="person" hint="Hauptkontakt für diese Buchung">
          <EField label="Anrede" value={bk.anrede} editing={editing} type="select" options={ANREDEN} onChange={(v) => patch({ anrede: v })} />
          <EField label="Vorname" value={bk.vorname} editing={editing} onChange={(v) => patch({ vorname: v })} />
          <EField label="Nachname"    value={bk.nachname} editing={editing} onChange={v => patch({ nachname: v })}/>
          <EField label="Straße" value={bk.adresse_strasse} editing={editing} onChange={(v) => patch({ adresse_strasse: v })} />
          <EField label="PLZ" value={bk.adresse_plz} editing={editing} onChange={(v) => patch({ adresse_plz: v })} />
          <EField label="Ort" value={bk.adresse_ort} editing={editing} onChange={(v) => patch({ adresse_ort: v })} />
          <EField label="Land" value={bk.adresse_land} editing={editing} onChange={(v) => patch({ adresse_land: v })} />
        </FieldGroup>
        <FieldGroup title="Erreichbarkeit" icon="contact_mail" hint={undefined}>
          <EField label="E-Mail"      value={bk.email}    editing={editing} onChange={v => patch({ email: v })}/>
          <EField label="Telefon" value={bk.telefon} editing={editing} type="tel" onChange={(v) => patch({ telefon: v })} />
        </FieldGroup>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Buchung-Tab
// ---------------------------------------------------------------------------

function EinheitenMultiSelect({
  selectedIds,
  units,
  onChange,
}: {
  selectedIds: number[];
  units: Unit[];
  onChange: (ids: number[]) => void;
}) {
  const toggle = (id: number) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    if (next.length > 0) onChange(next);
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
      {units.map((u) => {
        const active = selectedIds.includes(u.id);
        return (
          <button
            key={u.id}
            type="button"
            onClick={() => toggle(u.id)}
            style={{
              padding: "5px 12px",
              borderRadius: 6,
              border: `1.5px solid ${active ? "var(--brand-700)" : "var(--border)"}`,
              background: active ? "var(--brand-100)" : "var(--bg-subtle)",
              color: active ? "var(--brand-900)" : "var(--fg-muted)",
              fontFamily: "var(--font-body)",
              fontSize: 13,
              fontWeight: active ? 600 : 400,
              cursor: "pointer",
              transition: "all .12s",
            }}
          >
            {u.name}
          </button>
        );
      })}
    </div>
  );
}

function BuchungTab({
  bk,
  n,
  editing,
  patch,
}: {
  bk: Booking;
  n: number;
  editing: boolean;
  patch: (p: BookingPatch) => void;
}) {
  const STATI = WORKFLOW_STATUS;
  const SAISON = SAISONS;

  const { data: units = [] } = useQuery<Unit[]>({
    queryKey: ["units"],
    queryFn: fetchUnits,
    staleTime: Infinity,
  });

  const selectedIds = bk.einheiten_ids ?? [];
  const einheitenLabel = bk.einheiten_namen.length > 0
    ? bk.einheiten_namen.join(", ")
    : "—";

  return (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <FieldGroup title="Aufenthalt" icon="" hint="Zeitraum und Saison">
        <EField
          label="Check-in Datum"
          value={bk.check_in}
          editing={editing}
          type="date"
          display={formatDatum(bk.check_in)}
          onChange={(v) => {
            const { from, to } = syncDateRange(
              { from: bk.check_in, to: bk.check_out },
              "from",
              v,
              { minNights: 1, keepDuration: true },
            );
            patch({ check_in: from, check_out: to });
          }}
          />
          <EField
          label="Check-in Uhrzeit"
          value={bk.check_in_time || "15:00"}
          editing={editing}
          type="time"
          onChange={(v) => patch({ check_in_time: v })}
          />
          <EField label="Nächte" value={String(n)} editing={false} />        
          <EField
          label="Check-out Datum"
          value={bk.check_out}
          editing={editing}
          type="date"
          display={formatDatum(bk.check_out)}
          onChange={(v) => {
            const { from, to } = syncDateRange(
              { from: bk.check_in, to: bk.check_out },
              "to",
              v,
              { minNights: 1, keepDuration: true },
            );
            patch({ check_in: from, check_out: to });
          }}
          />
          <EField
          label="Check-out Uhrzeit"
          value={bk.check_out_time || "10:00"}
          editing={editing}
          type="time"
          onChange={(v) => patch({ check_out_time: v })}
          />
          <EField
          label="Saison"
          value={bk.saison}
          editing={editing}
          type="select"
          options={SAISON}
          onChange={(v) => patch({ saison: v })}
          />
      </FieldGroup>

      <FieldGroup
        title="Einheiten"
        icon=""
        footer={
          <Card style={{ marginTop: 12 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--fg-subtle)", fontWeight: 600, marginBottom: 2 }}>
              Einheiten
            </div>
            {editing ? (
              <EinheitenMultiSelect
                selectedIds={selectedIds}
                units={units}
                onChange={(ids) => patch({ einheiten_ids: ids })}
              />
            ) : (
              <div style={{ fontSize: 13, marginTop: 4 }}>{einheitenLabel}</div>
            )}
          </Card>
        }
      />

      <FieldGroup title="Verwaltung" icon="" hint="Status und Referenzen">
        <EField
          label="Buchungsdatum"
          value={bk.buchungsdatum}
          editing={editing}
          type="date"
          display={bk.buchungsdatum ? formatDatum(bk.buchungsdatum) : "—"}
          onChange={(v) => patch({ buchungsdatum: v })}
        />
        <EField label="Referenz" value={bk.buchungsreferenz || `#${bk.id}`} editing={false} />
        <EField label="Status" value={bk.status} editing={editing} type="select" options={STATI} onChange={(v) => patch({ status: v })} />
        <EField label="Gruppenart" type="select" options={GRUPPENARTEN} value={bk.gruppenart} editing={editing} onChange={(v) => patch({ gruppenart: v })} />
        <EField label="Gasttyp" value={bk.gast_typ || "Standard"} editing={false} />
        {bk.status === "Storniert" && (
          <EField label="Stornierungsdatum" value={bk.storniert_am ? formatDatum(bk.storniert_am) : "—"} editing={false}/>
        )}
      </FieldGroup>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Konus-Tab
// ---------------------------------------------------------------------------

type KonusProposal = {
  id: number;
  booking_id: number;
  submitted_at: string;
  status: string;
  personen_ab_16: number;
  kinder_6_bis_15: number;
  kinder_unter_6: number;
  anrede: string | null;
  vorname: string | null;
  nachname: string | null;
  geburtsdatum: string | null;
  adresse_strasse: string | null;
  adresse_hausnummer: string | null;
  adresse_plz: string | null;
  adresse_ort: string | null;
  adresse_land: string | null;
};

function KonusTab({
  bk,
  bookingId,
  editing,
  patch,
  onToast,
}: {
  bk: Booking;
  bookingId: number;
  editing: boolean;
  patch: (p: BookingPatch) => void;
  onToast: (msg: string, ok?: boolean) => void;
}) {

  const ANREDE_GRUPPENLEITER = ANREDEN_KONUS
  const queryClient = useQueryClient();
  const numOrZero = (v: string) => Number(v) || 0;
  const gesamt = (bk.personen_ab_16 ?? 0) + (bk.kinder_6_bis_15 ?? 0) + (bk.kinder_unter_6 ?? 0) || bk.gaesteanzahl;

  const { data: proposals = [] as KonusProposal[] } = useQuery<KonusProposal[]>({
    queryKey: ["konus-proposals", bookingId],
    queryFn: () => apiFetch<KonusProposal[]>(`/api/bookings/${bookingId}/konus-proposals`),
    retry: false,
  });

  const pendingProposals = proposals.filter((p) => p.status === "pending");

  const acceptMutation = useMutation({
    mutationFn: (proposalId: number) =>
      apiFetch(`/api/bookings/${bookingId}/konus-proposals/${proposalId}/accept`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["konus-proposals", bookingId] });
      queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      onToast("Konus-Daten übernommen.");
    },
    onError: (e: Error) => onToast(e.message, false),
  });

  const rejectMutation = useMutation({
    mutationFn: (proposalId: number) =>
      apiFetch(`/api/bookings/${bookingId}/konus-proposals/${proposalId}/reject`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["konus-proposals", bookingId] });
      onToast("Vorschlag verworfen.");
    },
    onError: (e: Error) => onToast(e.message, false),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Vorschlags-Banner */}
      {pendingProposals.map((p) => (
        <Card key={p.id} style={{ border: "1.5px solid var(--status-konus, #8558c8)", background: "color-mix(in oklab, var(--status-konus, #8558c8) 6%, var(--bg-card))" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--status-konus, #8558c8)" }}>assignment_ind</span>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Neuer Vorschlag vom Gast</div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginLeft: "auto" }}>
              Eingegangen: {formatDatum(p.submitted_at)}
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--fg-muted)", marginBottom: 4 }}>Personenzahlen</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px 16px", fontSize: 12, marginBottom: 10 }}>
            <div><span style={{ color: "var(--fg-muted)" }}>ab 16: </span><strong>{p.personen_ab_16}</strong></div>
            <div><span style={{ color: "var(--fg-muted)" }}>6–15: </span><strong>{p.kinder_6_bis_15}</strong></div>
            <div><span style={{ color: "var(--fg-muted)" }}>unter 6: </span><strong>{p.kinder_unter_6}</strong></div>
          </div>
          {(p.vorname || p.nachname || p.geburtsdatum || p.adresse_strasse) && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--fg-muted)", marginBottom: 4 }}>Gruppenleiter/in</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px 16px", fontSize: 12, marginBottom: 12 }}>
                {p.anrede && <div><span style={{ color: "var(--fg-muted)" }}>Anrede: </span><strong>{p.anrede}</strong></div>}
                {p.vorname && <div><span style={{ color: "var(--fg-muted)" }}>Vorname: </span><strong>{p.vorname}</strong></div>}
                {p.nachname && <div><span style={{ color: "var(--fg-muted)" }}>Nachname: </span><strong>{p.nachname}</strong></div>}
                {p.geburtsdatum && <div><span style={{ color: "var(--fg-muted)" }}>Geburtsdatum: </span><strong>{formatDatum(p.geburtsdatum)}</strong></div>}
                {p.adresse_strasse && <div style={{ gridColumn: "span 2" }}><span style={{ color: "var(--fg-muted)" }}>Adresse: </span><strong>{[p.adresse_strasse + (p.adresse_hausnummer ? ` ${p.adresse_hausnummer}` : ""), p.adresse_plz, p.adresse_ort].filter(Boolean).join(", ")}</strong></div>}
              </div>
            </>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => acceptMutation.mutate(p.id)}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
              style={{
                padding: "6px 14px", borderRadius: 6, border: "none",
                background: "var(--status-konus, #8558c8)", color: "#fff",
                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
              }}
            >
              Daten übernehmen
            </button>
            <button
              onClick={() => rejectMutation.mutate(p.id)}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
              style={{
                padding: "6px 14px", borderRadius: 6,
                border: "1px solid var(--border)", background: "var(--bg-card)",
                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
                color: "var(--fg-muted)",
              }}
            >
              Verwerfen
            </button>
          </div>
        </Card>
      ))}

      {/* Felder */}
      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        <FieldGroup title="Teilnehmer" icon="" hint="Aufteilung nach Altersgruppen">
          <EField
              label="Personen ab 16"
              value={bk.personen_ab_16}
              editing={editing}
              type="number"
              onChange={(v) => patch({ personen_ab_16: numOrZero(v) })}
            />
            <EField
            label="Kinder 6–15"
            value={bk.kinder_6_bis_15}
            editing={editing}
            type="number"
            onChange={(v) => patch({ kinder_6_bis_15: numOrZero(v) })}
            />
            <EField
            label="Kinder unter 6"
            value={bk.kinder_unter_6}
            editing={editing}
            type="number"
            onChange={(v) => patch({ kinder_unter_6: numOrZero(v) })}
            />
            <EField label="Gäste gesamt" value={String(gesamt)} editing={false} />
        </FieldGroup>

        <FieldGroup title="Gruppenleiter" icon="" hint="Kontaktinformationen">
          <EField
            label="Anrede Gruppenleiter/in"
            value={bk.gruppenleiter_anrede ?? ""}
            editing={editing}
            type="select"
            options={ANREDE_GRUPPENLEITER}
            onChange={(v) => patch({ gruppenleiter_anrede: v || null } as BookingPatch)}
            />
            <EField
            label="Vorname Gruppenleiter/in"
            value={bk.gruppenleiter_vorname ?? ""}
            editing={editing}
            onChange={(v) => patch({ gruppenleiter_vorname: v } as BookingPatch)}
            />
            <EField
            label="Nachname Gruppenleiter/in"
            value={bk.gruppenleiter_nachname ?? ""}
            editing={editing}
            onChange={(v) => patch({ gruppenleiter_nachname: v } as BookingPatch)}
            />
            <EField
            label="Straße"
            value={bk.gruppenleiter_adresse_strasse ?? ""}
            editing={editing}
            onChange={(v) => patch({ gruppenleiter_adresse_strasse: v } as BookingPatch)}
            />
            <EField
            label="Hausnummer"
            value={bk.gruppenleiter_adresse_hausnummer ?? ""}
            editing={editing}
            onChange={(v) => patch({ gruppenleiter_adresse_hausnummer: v } as BookingPatch)}
            />
            <EField
            label="PLZ"
            value={bk.gruppenleiter_adresse_plz ?? ""}
            editing={editing}
            onChange={(v) => patch({ gruppenleiter_adresse_plz: v } as BookingPatch)}
            />
            <EField
            label="Ort"
            value={bk.gruppenleiter_adresse_ort ?? ""}
            editing={editing}
            onChange={(v) => patch({ gruppenleiter_adresse_ort: v } as BookingPatch)}
            />
            <EField
            label="Land"
            value={bk.gruppenleiter_adresse_land ?? ""}
            editing={editing}
            onChange={(v) => patch({ gruppenleiter_adresse_land: v } as BookingPatch)}
            />
            <EField
            label="Geburtsdatum"
            value={bk.gruppenleiter_geburtsdatum ?? ""}
            editing={editing}
            type="date"
            display={bk.gruppenleiter_geburtsdatum ? formatDatum(bk.gruppenleiter_geburtsdatum) : "—"}
            onChange={(v) => patch({ gruppenleiter_geburtsdatum: v } as BookingPatch)}
            />
        </FieldGroup>

        <FieldGroup title="Meldeschein" icon="" hint="">
          <EField
            label="Meldescheinnummer"
            value={bk.meldescheinnummer ?? ""}
            editing={editing}
            onChange={(v) => patch({ meldescheinnummer: v || null } as BookingPatch)}
            display={bk.meldescheinnummer || "—"}
            />
        </FieldGroup>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ablage-Tab
// ---------------------------------------------------------------------------

type AblageSection = {
  id: "buchungsbestaetigung" | "infos_gruppe" | "rechnung" | "konus_meldung" | "meldeschein";
  icon: string;
  title: string;
  desc: string;
};

const ABLAGE_SECTIONS: AblageSection[] = [
  { id: "buchungsbestaetigung", icon: "description",    title: "Buchungsbestätigung", desc: "Offizielle Bestätigung für die Gruppe" },
  { id: "infos_gruppe",         icon: "info",           title: "Infos an Gruppe",     desc: "Anfahrt, Hausordnung, Schlüsselcode" },
  { id: "rechnung",             icon: "receipt_long",   title: "Rechnung",             desc: "Erzeugte Rechnungen und Zahlungsbelege" },
  { id: "konus_meldung",        icon: "assignment_ind", title: "Konus-Meldung",        desc: "XML-Meldung an die Gemeinde" },
  { id: "meldeschein",          icon: "fact_check",     title: "Meldeschein",          desc: "Von der Gemeinde ausgestellter Meldeschein (PDF)" },
];

function AblageTab({
  bookingId,
  onToast,
  onCreateRechnung,
}: {
  bookingId: number;
  onToast: (msg: string, ok?: boolean) => void;
  onCreateRechnung: () => void;
}) {
  const queryClient = useQueryClient();

  const { data: files = [] as AblageFile[], isLoading } = useQuery({
    queryKey: ["ablage", bookingId],
    queryFn: () => fetchAblage(bookingId),
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ path }: { path: string }) => deleteAblageFile(path, bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ablage", bookingId] });
      onToast("Datei gelöscht");
    },
    onError: (err: Error) => onToast(err.message, false),
  });

  const [emailDraftPending, setEmailDraftPending] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<AblageFile | null>(null);
  const meldescheinInputRef = useRef<HTMLInputElement | null>(null);
  const [meldescheinPending, setMeldescheinPending] = useState(false);

  const handleMeldescheinUpload = async (file: File) => {
    if (file.type && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      onToast("Nur PDF-Dateien sind erlaubt.", false);
      return;
    }
    setMeldescheinPending(true);
    try {
      await uploadMeldeschein(bookingId, file);
      queryClient.invalidateQueries({ queryKey: ["ablage", bookingId] });
      onToast("Meldeschein hochgeladen.");
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : "Upload fehlgeschlagen.", false);
    } finally {
      setMeldescheinPending(false);
    }
  };

  const handleEmailDraft = async (art: string) => {
    setEmailDraftPending(true);
    try {
      await downloadEmailDraft(bookingId, art);
      onToast("E-Mail-Entwurf heruntergeladen — zum Öffnen doppelklicken.");
    } catch (e: unknown) {
      onToast((e instanceof Error ? e.message : "Fehler"), false);
    } finally {
      setEmailDraftPending(false);
    }
  };

  if (isLoading) {
    return <div style={{ padding: 24, fontSize: 13, color: "var(--fg-muted)" }}>Lade…</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <input
        ref={meldescheinInputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: "none" }}
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) void handleMeldescheinUpload(f);
          e.target.value = "";
        }}
      />
      {deleteConfirm && (
        <ConfirmDialog
          icon="delete"
          title="Datei löschen"
          body={<>Datei <strong>{deleteConfirm.name}</strong> wirklich unwiderruflich löschen?</>}
          confirmLabel="Löschen"
          onConfirm={() => { deleteMutation.mutate({ path: deleteConfirm.path }); setDeleteConfirm(null); }}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
      {ABLAGE_SECTIONS.map((s) => {
        const sektion_files = files.filter((f: AblageFile) => f.dokument_art === s.id);
        return (
          <Card key={s.id} padded={false}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 18px",
              borderBottom: sektion_files.length ? "1px solid var(--border)" : "none",
            }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "var(--brand-100)",
                color: "var(--brand-900)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <span className="material-icons-outlined" style={{ fontSize: 18 }}>{s.icon}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{s.title}</div>
                <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>{s.desc}</div>
              </div>
              <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>
                {sektion_files.length} {sektion_files.length === 1 ? "Datei" : "Dateien"}
              </span>
              {s.id === "rechnung" && (
                <Btn variant="primary" icon="add" size="sm" onClick={onCreateRechnung}>
                  Rechnung erstellen
                </Btn>
              )}
              {s.id === "meldeschein" && (
                <Btn
                  variant="primary"
                  icon="upload_file"
                  size="sm"
                  onClick={() => meldescheinInputRef.current?.click()}
                  disabled={meldescheinPending}
                >
                  {meldescheinPending ? "Lädt hoch…" : "Meldeschein hochladen"}
                </Btn>
              )}
            </div>

            {sektion_files.length > 0 ? sektion_files.map((f, i) => (
              <DocumentRow
                key={f.path}
                file={f}
                isLast={i === sektion_files.length - 1}
                onDelete={() => setDeleteConfirm(f)}
                onMail={s.id === "buchungsbestaetigung" || s.id === "infos_gruppe"
                  ? () => handleEmailDraft(s.id === "buchungsbestaetigung" ? "bestaetigung" : "infos_gruppe")
                  : undefined}
                disabled={deleteMutation.isPending || emailDraftPending}
              />
            )) : (
              <div style={{
                padding: 18,
                textAlign: "center",
                fontSize: 12,
                color: "var(--fg-muted)",
                background: "var(--bg-subtle)",
              }}>
                Noch keine {s.title} erzeugt.
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function DocumentRow({
  file,
  isLast,
  onDelete,
  onMail,
  disabled,
}: {
  file: AblageFile;
  isLast: boolean;
  onDelete: () => void;
  onMail?: () => void | Promise<void>;
  disabled: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const datum = file.erstellt_am
    ? new Date(file.erstellt_am).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "";
  const groesseKb = (file.groesse / 1024).toFixed(0);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAblageFile(file.path, file.name);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "Download fehlgeschlagen");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "11px 18px",
      borderBottom: isLast ? "none" : "1px solid var(--border)",
      position: "relative",
    }}>
      <span className="material-icons-outlined" style={{ fontSize: 20, color: "var(--fg-subtle)" }}>
        picture_as_pdf
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {file.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
          {groesseKb} KB{datum ? ` · ${datum}` : ""}
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={downloading}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "5px 8px",
          borderRadius: 6,
          color: "var(--brand-700)",
          background: "none",
          border: "none",
          cursor: downloading ? "wait" : "pointer",
          fontSize: 12,
        }}
        title="Herunterladen"
      >
        <span className="material-icons-outlined" style={{ fontSize: 18 }}>download</span>
      </button>
      <button
        onClick={() => setMenuOpen((o) => !o)}
        disabled={disabled}
        style={{
          background: "none",
          border: "none",
          cursor: disabled ? "default" : "pointer",
          padding: 4,
          color: "var(--fg-subtle)",
        }}
        title="Mehr Aktionen"
      >
        <span className="material-icons-outlined" style={{ fontSize: 20 }}>more_horiz</span>
      </button>
      {menuOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 8,
            zIndex: 5,
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "var(--sh-2)",
            minWidth: 180,
            padding: 4,
          }}
          onMouseLeave={() => setMenuOpen(false)}
        >
          {onMail && (
            <button
              onClick={() => { setMenuOpen(false); onMail(); }}
              style={MENU_ITEM_STYLE}
            >
              <span className="material-icons-outlined" style={{ fontSize: 16 }}>send</span>
              Per E-Mail senden
            </button>
          )}
          <button
            onClick={() => { setMenuOpen(false); onDelete(); }}
            style={{ ...MENU_ITEM_STYLE, color: "var(--status-cancel)" }}
          >
            <span className="material-icons-outlined" style={{ fontSize: 16 }}>delete</span>
            Löschen
          </button>
        </div>
      )}
    </div>
  );
}

const MENU_ITEM_STYLE = {
  display: "flex" as const,
  alignItems: "center" as const,
  gap: 8,
  width: "100%",
  padding: "8px 10px",
  background: "none",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 13,
  fontFamily: "var(--font-body)",
  textAlign: "left" as const,
  color: "var(--fg)",
};

// ---------------------------------------------------------------------------
// Historie-Tab
// ---------------------------------------------------------------------------

function HistorieTab({ bookingId }: { bookingId: number }) {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["verlauf", bookingId],
    queryFn: () => fetchVerlauf(bookingId),
    retry: false,
  });

  const AKTION_ICON: Record<string, string> = {
    "Workflow": "check_circle",
    "Buchungsbestätigung erstellt (ODT→PDF)": "description",
    "Infos an Gruppe erstellt (ODT→PDF)": "description",
    "Mail": "email",
    "Angelegt": "add_circle",
  };

  const iconForAktion = (aktion: string): string => {
    for (const [key, icon] of Object.entries(AKTION_ICON)) {
      if (aktion.includes(key)) return icon;
    }
    return "history";
  };

  if (isLoading) {
    return <div style={{ padding: 24, fontSize: 13, color: "var(--fg-muted)" }}>Lade…</div>;
  }

  if (entries.length === 0) {
    return (
      <Card style={{ padding: 40, textAlign: "center" }}>
        <span className="material-icons-outlined" style={{ fontSize: 40, color: "var(--fg-subtle)" }}>history</span>
        <div style={{ fontSize: 15, fontWeight: 600, marginTop: 10 }}>Kein Verlauf</div>
        <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}>
          Aktionen wie Statusänderungen und Mailversand werden hier protokolliert.
        </div>
      </Card>
    );
  }

  return (
    <Card padded={false}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", fontSize: 14, fontWeight: 600 }}>
        Verlauf
      </div>
      <div style={{ padding: "8px 0" }}>
        {entries.map((e: VerlaufEntry, i: number) => (
          <div key={e.id} style={{ display: "flex", gap: 14, padding: "12px 18px", position: "relative" }}>
            {i < entries.length - 1 && (
              <div style={{
                position: "absolute",
                left: 33,
                top: 40,
                bottom: 0,
                width: 1,
                background: "var(--border)",
              }} />
            )}
            <div style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "var(--brand-100)",
              color: "var(--brand-900)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: "2px solid var(--bg-card)",
              zIndex: 1,
            }}>
              <span className="material-icons-outlined" style={{ fontSize: 16 }}>
                {iconForAktion(e.aktion)}
              </span>
            </div>
            <div style={{ flex: 1, paddingTop: 4 }}>
              <div style={{ fontSize: 13 }}>
                {e.aktion}
                {e.details && <span style={{ color: "var(--fg-muted)", marginLeft: 6 }}>— {e.details}</span>}
              </div>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
                {e.zeitpunkt ? new Date(e.zeitpunkt).toLocaleString("de-DE") : "—"}
                {e.akteur && ` · ${e.akteur}`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Kleine Hilfskomponenten
// ---------------------------------------------------------------------------

function Tile({ label, value, meta }: { label: string; value: string; meta?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--fg-subtle)", fontWeight: 600 }}>
        {label}
      </div>
      <div className="display" style={{ fontSize: 22, color: "var(--fg)", marginTop: 2, lineHeight: 1.2 }}>
        {value}
      </div>
      {meta && <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>{meta}</div>}
    </div>
  );
}

function InfoCell({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div style={{
      padding: "12px 18px",
      gridColumn: wide ? "1 / -1" : "auto",
      borderRight: "1px solid var(--border)",
      borderBottom: "1px solid var(--border)",
    }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--fg-subtle)", fontWeight: 600, marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 13 }}>{value}</div>
    </div>
  );
}

function RechnungCard({
  bk,
  editing,
  patch,
  onAnzahlungConfirm,
  anzahlungPending,
  defaultAnzahlung,
  onTransition,
  transitionPending,
  setResetConfirm,
  resetVersandtPending,
}: {
  bk: Booking;
  editing: boolean;
  patch: (p: BookingPatch) => void;
  onAnzahlungConfirm: (erhalten: boolean) => void;
  anzahlungPending: boolean;
  defaultAnzahlung: number;
  onTransition: (target: string) => void;
  transitionPending: boolean;
  setResetConfirm: (v: { label: string; felder: BookingPatch }) => void;
  resetVersandtPending: boolean;
}) {
  const anzahlungBetrag = bk.anzahlung_betrag ?? 0;
  const hatAnzahlung = anzahlungBetrag > 0;
  const erhalten = bk.anzahlung_erhalten ?? false;

  const statusVorRechnung = ["Angelegt", "Bestätigt", "Konus Link versandt", "Konus-Daten eingegeben", "Infos an Gruppe versandt", "Abgereist"];
  const gesamtpreis = bk.rechnungsbetrag_gesamt ?? 0;
  const zeigeKeineRechnung = gesamtpreis === 0 && statusVorRechnung.includes(bk.status);

  const subHeaderStyle = {
    fontSize: 11,
    textTransform: "uppercase" as const,
    letterSpacing: ".08em",
    color: "var(--fg-subtle)",
    fontWeight: 600,
    marginBottom: 6,
  };
  const mutedValueStyle = {
    fontSize: 13,
    color: "var(--fg-muted)",
    fontStyle: "italic" as const,
  };
  const betragStyle = {
    fontSize: 18,
    color: "var(--brand-900)",
  };

  return (
    <Card padded={false}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", fontSize: 14, fontWeight: 600 }}>
        Rechnung
      </div>

      {/* Subpunkt: Anzahlung */}
      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={subHeaderStyle}>Anzahlung</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          {editing ? (
            <EurInput
              value={hatAnzahlung ? anzahlungBetrag : defaultAnzahlung}
              onChange={(val) => patch({ anzahlung_betrag: val })}
              style={{
                width: 90,
                padding: "4px 8px",
                borderRadius: 6,
                border: "1.5px solid var(--brand-700)",
                background: "var(--bg-input, var(--bg-subtle))",
                color: "var(--fg)",
                fontSize: 13,
                fontFamily: "var(--font-body)",
              }}
            />
          ) : hatAnzahlung ? (
            <span className="display" style={betragStyle}>{anzahlungBetrag.toFixed(2)} €</span>
          ) : (
            <span style={mutedValueStyle}>keine Anzahlung hinterlegt</span>
          )}
          <button
            onClick={() => !anzahlungPending && onAnzahlungConfirm(!erhalten)}
            disabled={anzahlungPending}
            title={erhalten ? "Eingang widerrufen" : "Eingang bestätigen"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: erhalten ? "var(--status-ok, #22c55e)" : "var(--bg-subtle)",
              border: `1.5px solid ${erhalten ? "var(--status-ok, #22c55e)" : "var(--border)"}`,
              borderRadius: 6,
              padding: "4px 10px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              color: erhalten ? "#fff" : "var(--fg-muted)",
              fontFamily: "var(--font-body)",
              transition: "all .15s",
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: 16 }}>
              {erhalten ? "check_circle" : "radio_button_unchecked"}
            </span>
            {erhalten
              ? `Eingegangen${bk.anzahlung_erhalten_am ? `: ${formatDatum(bk.anzahlung_erhalten_am)}` : ""}`
              : "Eingang bestätigen"}
          </button>
        </div>
      </div>

      {/* Subpunkt: Gesamtpreis */}
      <div style={{ padding: "12px 16px" }}>
        <div style={subHeaderStyle}>Gesamtpreis</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          {zeigeKeineRechnung ? (
            <span style={mutedValueStyle}>noch keine Rechnung</span>
          ) : (
            <span className="display" style={betragStyle}>{gesamtpreis.toFixed(2)} €</span>
          )}
          {bk.status === "Bezahlt" ? (
            <button
              onClick={() => setResetConfirm({ label: "Rechnung bezahlt", felder: { bezahlt_am: null, status: "Rechnung erstellt" } })}
              disabled={resetVersandtPending}
              title="Klicken zum Zurücksetzen"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "var(--status-ok, #22c55e)", color: "#fff",
                border: "none", borderRadius: 6, padding: "4px 10px",
                fontSize: 12, fontWeight: 600,
                cursor: resetVersandtPending ? "default" : "pointer",
                opacity: resetVersandtPending ? 0.6 : 1,
                fontFamily: "var(--font-body)",
              }}
            >
              <span className="material-icons-outlined" style={{ fontSize: 16 }}>check_circle</span>
              Eingegangen{bk.bezahlt_am ? `: ${formatDatum(bk.bezahlt_am)}` : ""}
            </button>
          ) : bk.status === "Rechnung erstellt" ? (
            <button
              onClick={() => onTransition("Bezahlt")}
              disabled={transitionPending}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "var(--bg-subtle)", color: "var(--fg-muted)",
                border: "none", borderRadius: 6, padding: "4px 10px",
                fontSize: 12, fontWeight: 600,
                cursor: transitionPending ? "default" : "pointer",
                opacity: transitionPending ? 0.6 : 1,
                fontFamily: "var(--font-body)",
              }}
            >
              <span className="material-icons-outlined" style={{ fontSize: 16 }}>radio_button_unchecked</span>
              Eingang bestätigen
            </button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function SchluesselCell({ label, value, onChange }: { label: string; value: string | null; onChange: (v: string) => void }) {
  return (
    <div style={{ padding: "10px 18px", borderRight: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
      <EField label={label} value={value} editing onChange={onChange} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Konus-Meldung-Dialog
// ---------------------------------------------------------------------------

const PFLICHTFELDER_LABELS: Record<string, string> = {
  gruppenleiter_anrede: "Anrede Gruppenleiter/in",
  gruppenleiter_vorname: "Vorname Gruppenleiter/in",
  gruppenleiter_nachname: "Nachname Gruppenleiter/in",
  gruppenleiter_geburtsdatum: "Geburtsdatum",
  gruppenleiter_adresse_strasse: "Straße",
  gruppenleiter_adresse_hausnummer: "Hausnummer",
  gruppenleiter_adresse_plz: "PLZ",
  gruppenleiter_adresse_ort: "Ort",
  check_in: "Anreisedatum",
  check_out: "Abreisedatum",
  "personen (ab_16 / 6_bis_15 / unter_6 alle 0)": "Personenanzahl (mind. 1)",
};

function pruefePflichtfelder(bk: Booking): string[] {
  const fehlend: string[] = [];
  const check = (key: keyof Booking, label: string) => {
    const v = bk[key];
    if (!v || (typeof v === "string" && !v.trim())) fehlend.push(label);
  };
  check("gruppenleiter_anrede", PFLICHTFELDER_LABELS.gruppenleiter_anrede);
  check("gruppenleiter_vorname", PFLICHTFELDER_LABELS.gruppenleiter_vorname);
  check("gruppenleiter_nachname", PFLICHTFELDER_LABELS.gruppenleiter_nachname);
  check("gruppenleiter_geburtsdatum", PFLICHTFELDER_LABELS.gruppenleiter_geburtsdatum);
  check("gruppenleiter_adresse_strasse", PFLICHTFELDER_LABELS.gruppenleiter_adresse_strasse);
  check("gruppenleiter_adresse_hausnummer", PFLICHTFELDER_LABELS.gruppenleiter_adresse_hausnummer);
  check("gruppenleiter_adresse_plz", PFLICHTFELDER_LABELS.gruppenleiter_adresse_plz);
  check("gruppenleiter_adresse_ort", PFLICHTFELDER_LABELS.gruppenleiter_adresse_ort);
  const hatPersonen = (bk.personen_ab_16 ?? 0) > 0 || (bk.kinder_6_bis_15 ?? 0) > 0 || (bk.kinder_unter_6 ?? 0) > 0;
  if (!hatPersonen) fehlend.push(PFLICHTFELDER_LABELS["personen (ab_16 / 6_bis_15 / unter_6 alle 0)"]);
  return fehlend;
}

function KonusMeldungDialog({
  bookingId,
  onClose,
  onSuccess,
  onToast,
}: {
  bookingId: number;
  onClose: () => void;
  onSuccess: () => void;
  onToast: (msg: string, ok?: boolean) => void;
}) {
  const [meldescheinnummer, setMeldescheinnummer] = useState("");
  const [pending, setPending] = useState(false);

  async function handleSubmit() {
    if (!meldescheinnummer.trim()) return;
    setPending(true);
    try {
      await apiFetch<{ detail: string }>(
        `/api/bookings/${bookingId}/konus-meldung`,
        { method: "POST", body: { meldescheinnummer: meldescheinnummer.trim() } },
      );
      onSuccess();
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : "Fehler beim Eintragen.", false);
      setPending(false);
    }
  }

  const overlay: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  };
  const box: React.CSSProperties = {
    background: "var(--bg-card)", borderRadius: 12, padding: 24, width: "min(440px,100%)",
    boxShadow: "0 8px 32px rgba(0,0,0,.25)", display: "flex", flexDirection: "column", gap: 16,
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="material-icons-outlined" style={{ fontSize: 22, color: "var(--status-konus, #8558c8)" }}>assignment_turned_in</span>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Konus-Daten bei Behörde eintragen</div>
        </div>

        <>
          <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
            Bitte die vom System der Gemeinde vergebene <strong>Meldescheinnummer</strong> eingeben. Anschließend wird der Status auf <em>Konus-Daten eingegeben</em> gesetzt.
          </div>
          <div style={{ fontSize: 12, color: "var(--note-fg)", background: "var(--note-bg)", border: "1px solid var(--note-border)", borderRadius: 6, padding: "8px 10px" }}>
            <strong>Hinweis:</strong> Nach dem Eintragen muss der Meldeschein der Gemeinde als PDF in der Ablage hochgeladen werden.
          </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--fg-muted)", display: "block", marginBottom: 4 }}>
                Meldescheinnummer *
              </label>
              <input
                type="text"
                value={meldescheinnummer}
                onChange={e => setMeldescheinnummer(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !pending && meldescheinnummer.trim() && handleSubmit()}
                placeholder="z. B. 64350"
                autoFocus
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--fg)", fontSize: 14, fontFamily: "var(--font-body)", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={onClose}
                disabled={pending}
                style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-card)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)" }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSubmit}
                disabled={pending || !meldescheinnummer.trim()}
                style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: pending || !meldescheinnummer.trim() ? "var(--fg-muted)" : "var(--status-konus, #8558c8)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: pending || !meldescheinnummer.trim() ? "default" : "pointer", fontFamily: "var(--font-body)" }}
              >
                {pending ? "Trage ein…" : "Eintragen & Status setzen"}
              </button>
            </div>
        </>
      </div>
    </div>
  );
}
